/**
 * Стили как данные — docs/release-2026-09-19.md §5.
 *
 * ВЕРСИЯ СТИЛЯ — это набор файлов базы знаний (`knowledge/**.md`), которыми писатель и
 * мониторинг собирают промпт. Два вида:
 *   • `release` — стиль, выложенный вместе с кодом: файлы на диске, в базу не копируются,
 *     строка одна на бренд и следует за выкладкой (имя — `knowledge/style.json`);
 *   • `custom` — копия, заведённая человеком и правимая в интерфейсе («копировать в v+1»).
 *
 * Сервисы берут файлы копии по intake (`knowledge:read`) и раскладывают их в свой каталог
 * состояния; загрузчик базы знаний остаётся прежним и читает раскладку как обычный корень.
 * У релиза файлов по проводу нет: сервис читает свой каталог, как и до правки.
 *
 * Провод UI — `GET/POST /api/v1/styles*`, `GET/POST /api/v1/style-default`; провод сервисов —
 * `GET /intake/v1/style-default`, `GET /intake/v1/style-versions/:id`.
 */

import * as z from 'zod';
import { BRAND_RE } from '../approval-key/limits.ts';
import { UUID_V7_RE } from '../ids.ts';
import { ISO_UTC_MS } from '../time.ts';

const Uuid = z.string().regex(UUID_V7_RE);
const IsoUtcMs = z.string().regex(ISO_UTC_MS);
const Sha256Hex = z.string().regex(/^[0-9a-f]{64}$/);

/** Потолки — зеркало CHECK'ов `style_version`/`style_file` (migrations/0019), значением. */
export const STYLE_NAME_MAX = 40;
export const STYLE_NOTE_MAX = 500;
export const STYLE_FILE_BODY_MAX = 100_000;

/**
 * Путь файла версии — идентификатор инструкции без `.md`, ровно как в
 * `config/brands/<brand>.yaml` → knowledge.* (`voice/geoboost`, `core/system.writer`,
 * `formats/examples/geoboost/news/01`). Сегменты из строчной латиницы, цифр, `_`, `-` и
 * одиночных точек внутри; ни `..`, ни ведущего `/`, ни расширения — загрузчик требует
 * `frontmatter.id == путь`, и путь с расширением не совпал бы ни с одним id.
 */
export const STYLE_FILE_PATH_RE =
  /^[a-z0-9][a-z0-9_-]*(?:\.[a-z0-9_-]+)*(?:\/[a-z0-9][a-z0-9_-]*(?:\.[a-z0-9_-]+)*)*$/;

export const StyleFilePathSchema = z
  .string()
  .min(1)
  .max(200)
  .regex(STYLE_FILE_PATH_RE)
  .refine((p) => !p.endsWith('.md'), { error: 'путь файла стиля пишется без .md' });

/** Имя версии: то, что человек видит на чипе («v6», «v6 — короче абзацы»). */
export const StyleNameSchema = z
  .string()
  .min(1)
  .max(STYLE_NAME_MAX)
  .refine((s) => s.trim() === s && !/[\r\n\t]/.test(s), {
    error: 'имя версии — одна строка без пробелов по краям',
  });

/** Какие сервисы читают файл: по спискам `knowledge.writer` / `knowledge.monitor` бренда. */
export const STYLE_AGENTS = ['writer', 'monitor'] as const;
export const StyleAgentSchema = z.enum(STYLE_AGENTS);
export type StyleAgent = z.output<typeof StyleAgentSchema>;

/**
 * Группа файла в списке редактора — по первому сегменту пути. `examples` — примеры постов
 * (`formats/examples/**`): их читает писатель, но это не инструкции, и в списке они стоят
 * отдельно, в конце.
 */
export const STYLE_FILE_GROUPS = [
  'core',
  'voice',
  'formats',
  'publishing',
  'examples',
  'other',
] as const;
export const StyleFileGroupSchema = z.enum(STYLE_FILE_GROUPS);
export type StyleFileGroup = z.output<typeof StyleFileGroupSchema>;

export const StyleKindSchema = z.enum(['release', 'custom']);
export type StyleKind = z.output<typeof StyleKindSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Версии — GET /api/v1/styles?brand=

export const StyleVersionSchema = z.strictObject({
  id: Uuid,
  brand: z.string().min(1),
  kind: StyleKindSchema,
  name: z.string().min(1),
  /** От какой версии скопирована и её имя; у релиза — `null`. */
  base_id: Uuid.nullable(),
  base_name: z.string().nullable(),
  note: z.string().nullable(),
  content_sha256: Sha256Hex,
  files_count: z.int().nonnegative(),
  created_by_login: z.string().nullable(),
  created_at: IsoUtcMs,
  updated_at: IsoUtcMs,
  archived: z.boolean(),
  /** Эта версия — стиль бренда по умолчанию (с учётом «ничего не выбрано = релиз»). */
  is_default: z.boolean(),
});
export type StyleVersion = z.output<typeof StyleVersionSchema>;

export const StyleVersionsResponseSchema = z.strictObject({
  /** Релиз первым, затем копии — свежие первыми; скрытые копии — в конце. */
  versions: z.array(StyleVersionSchema),
  release_id: Uuid,
  default_id: Uuid,
  /**
   * Имя, которое экран предлагает новой копии: «v» и число на единицу больше наибольшего
   * числа среди имён вида «vN» (включая имя релиза). Предложение, а не правило: имя можно
   * переписать.
   */
  next_name: z.string().min(1),
});
export type StyleVersionsResponse = z.output<typeof StyleVersionsResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Файлы версии — GET /api/v1/styles/:id/files?brand=

export const StyleFileSummarySchema = z.strictObject({
  path: StyleFilePathSchema,
  /** Первый заголовок H1 файла; `null` — у примера заголовка нет. */
  title: z.string().nullable(),
  /** Слой из frontmatter; `null` — пример, не инструкция. */
  layer: z.enum(['A', 'B']).nullable(),
  agents: z.array(StyleAgentSchema),
  group: StyleFileGroupSchema,
  /** Длина файла целиком, в кодпоинтах. */
  chars: z.int().nonnegative(),
  sha256: Sha256Hex,
  /** Файл отличается от того же файла базовой версии (у релиза — всегда `false`). */
  changed: z.boolean(),
});
export type StyleFileSummary = z.output<typeof StyleFileSummarySchema>;

export const StyleFilesResponseSchema = z.strictObject({
  version: StyleVersionSchema,
  files: z.array(StyleFileSummarySchema),
});
export type StyleFilesResponse = z.output<typeof StyleFilesResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Один файл — GET /api/v1/styles/:id/file?brand=&path=

export const StyleFileSchema = z.strictObject({
  version_id: Uuid,
  path: StyleFilePathSchema,
  /** Файл целиком: frontmatter + тело, ровно как лежит на диске. */
  body: z.string().min(1),
  sha256: Sha256Hex,
  /** `false` — версия-релиз: правится только выкладкой, в интерфейсе — копией. */
  editable: z.boolean(),
  updated_at: IsoUtcMs.nullable(),
  updated_by_login: z.string().nullable(),
});
export type StyleFile = z.output<typeof StyleFileSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Правка файла — POST /api/v1/styles/:id/file

/**
 * `expected_sha256` — sha256 файла, который человек открыл. Две вкладки, правящие один
 * файл, не затирают друг друга молча: вторая получает 409 `style_file_stale` и перечитывает.
 */
export const SaveStyleFileBodySchema = z.strictObject({
  brand: z.string().regex(BRAND_RE),
  path: StyleFilePathSchema,
  body: z.string().min(1).max(STYLE_FILE_BODY_MAX),
  expected_sha256: Sha256Hex,
});
export type SaveStyleFileBody = z.output<typeof SaveStyleFileBodySchema>;

export const SaveStyleFileResponseSchema = z.strictObject({
  file: StyleFileSchema,
  version: StyleVersionSchema,
  /**
   * Нефатальные замечания проверки (тело длиннее 12 000 знаков, нет раздела «Правила» и
   * т. п.): версия сохранена, но человеку стоит знать. Фатальное — 422, и файл не сохранён.
   */
  warnings: z.array(z.string()),
});
export type SaveStyleFileResponse = z.output<typeof SaveStyleFileResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Копия — POST /api/v1/styles/clone

export const CloneStyleBodySchema = z.strictObject({
  brand: z.string().regex(BRAND_RE),
  base_id: Uuid,
  name: StyleNameSchema,
  note: z.string().min(1).max(STYLE_NOTE_MAX).nullable(),
});
export type CloneStyleBody = z.output<typeof CloneStyleBodySchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Переименовать / скрыть — POST /api/v1/styles/:id

export const UpdateStyleBodySchema = z.strictObject({
  brand: z.string().regex(BRAND_RE),
  name: StyleNameSchema.optional(),
  note: z.string().min(1).max(STYLE_NOTE_MAX).nullable().optional(),
  archived: z.boolean().optional(),
});
export type UpdateStyleBody = z.output<typeof UpdateStyleBodySchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Стиль по умолчанию — GET/POST /api/v1/style-default

export const StyleDefaultSchema = z.strictObject({
  brand: z.string().min(1),
  /** Выбор человека; `null` — не выбирали, работает релиз. */
  version_id: Uuid.nullable(),
  /** Что реально поедет в следующий прогон. */
  effective: StyleVersionSchema,
  changed_at: IsoUtcMs.nullable(),
  changed_by_login: z.string().nullable(),
  note: z.string().nullable(),
});
export type StyleDefault = z.output<typeof StyleDefaultSchema>;

/** `version_id: null` — вернуть умолчание к релизу (DELETE строки `style_default`). */
export const SetStyleDefaultBodySchema = z.strictObject({
  brand: z.string().regex(BRAND_RE),
  version_id: Uuid.nullable(),
  note: z.string().min(1).max(STYLE_NOTE_MAX).nullable(),
});
export type SetStyleDefaultBody = z.output<typeof SetStyleDefaultBodySchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Провод сервисов — GET /intake/v1/style-default, GET /intake/v1/style-versions/:id

/** Ссылка на версию: чем писать этот прогон. Сервис сверяет вид и при `custom` тянет файлы. */
export const StyleRefTaskSchema = z.strictObject({
  brand: z.string().min(1),
  version_id: Uuid,
  kind: StyleKindSchema,
  name: z.string().min(1),
  content_sha256: Sha256Hex,
});
export type StyleRefTask = z.output<typeof StyleRefTaskSchema>;

/**
 * Версия с файлами. У `release` список ПУСТ: файлы релиза лежат в каталоге базы знаний
 * самого сервиса, и гнать их по проводу значило бы завести второй путь к тем же байтам.
 */
export const StyleVersionFilesTaskSchema = z.strictObject({
  brand: z.string().min(1),
  version_id: Uuid,
  kind: StyleKindSchema,
  name: z.string().min(1),
  content_sha256: Sha256Hex,
  files: z.array(
    z.strictObject({
      path: StyleFilePathSchema,
      body: z.string().min(1).max(STYLE_FILE_BODY_MAX),
    }),
  ),
});
export type StyleVersionFilesTask = z.output<typeof StyleVersionFilesTaskSchema>;
